/**
 * Statistiques de corrosion agrégées par réseau et par matériau.
 *
 * Calcul PUR (aucune dépendance pdfmake) pour rester testable et éviter tout
 * cycle d'imports avec le moteur de rendu (`tiptap-to-pdfmake` <-> `pdf-sections`).
 *
 * Alimente la variable `{{mesures_min_max_moy}}` insérable dans les conclusions :
 * pour chaque couple (réseau x matériau) présent dans la campagne, on calcule
 * min / max / moyenne du pourcentage de corrosion. Le rendu coloré (code couleur
 * Renaud) est fait côté `tiptap-to-pdfmake`.
 *
 * Règle métier (Renaud, juin 2026) : on ne mélange jamais deux matériaux dans
 * une même moyenne (acier vs cuivre non comparables). D'où la maille réseau x
 * matériau, jamais une moyenne globale.
 */

import type { SiteExportData } from "@/modules/corrosion/export/data-fetcher";

export interface MaterialStat {
  materiau: string;
  /** Nombre de mesures (avec % de corrosion renseigné) dans ce couple */
  nb: number;
  min: number;
  max: number;
  /** Moyenne arrondie à 1 décimale */
  moyenne: number;
}

export interface ReseauStatGroup {
  reseau: string;
  materials: MaterialStat[];
}

const RESEAU_NON_PRECISE = "Réseau non précisé";
const MATERIAU_NON_PRECISE = "Matériau non précisé";

/**
 * Agrège les mesures de la campagne par réseau puis par matériau.
 * - Ignore les mesures absentes ou sans % de corrosion.
 * - Réseaux dans leur ordre d'apparition ; matériaux triés par nom.
 */
export function computeNetworkMaterialStats(data: SiteExportData): ReseauStatGroup[] {
  // reseau -> (materiau -> liste des % de corrosion)
  const byReseau = new Map<string, Map<string, number[]>>();
  const reseauOrder: string[] = [];

  for (const zoneData of data.zones) {
    for (const pinData of zoneData.pins) {
      const mesure = pinData.mesure;
      if (!mesure) continue;
      const pct = mesure.pourcentageCorrosion;
      if (pct === null || pct === undefined) continue;

      // Maille = TYPE de réseau uniquement (« Eau glacée », « Chauffage »...),
      // sans le sens (Aller/Retour) ni le complément libre : demande Renaud
      // (juillet 2026), la synthèse par libellé complet éclatait la conclusion
      // en lignes vertical / aller / retour trop granulaires.
      // Fallback legacy : localisation libre pour les mesures d'avant la
      // triade structurée.
      const reseau =
        mesure.reseauType?.trim() || mesure.localisation?.trim() || RESEAU_NON_PRECISE;
      const materiau = mesure.materiau?.trim() || MATERIAU_NON_PRECISE;

      let byMateriau = byReseau.get(reseau);
      if (!byMateriau) {
        byMateriau = new Map();
        byReseau.set(reseau, byMateriau);
        reseauOrder.push(reseau);
      }
      const values = byMateriau.get(materiau);
      if (values) values.push(pct);
      else byMateriau.set(materiau, [pct]);
    }
  }

  const groups: ReseauStatGroup[] = [];
  for (const reseau of reseauOrder) {
    const byMateriau = byReseau.get(reseau)!;
    const materials: MaterialStat[] = [];
    for (const [materiau, values] of byMateriau) {
      const sum = values.reduce((a, b) => a + b, 0);
      materials.push({
        materiau,
        nb: values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        moyenne: Math.round((sum / values.length) * 10) / 10,
      });
    }
    materials.sort((a, b) => a.materiau.localeCompare(b.materiau, "fr"));
    groups.push({ reseau, materials });
  }

  return groups;
}
